import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * /wallet — club-less entry to the Wallet (Week 4 store build, Sept 2026).
 *
 * The home-screen shortcut / quick action ("My club card") and any link that
 * doesn't know the person's club land here. The Wallet is the one surface for
 * signed-in people (offers, codes, and the card on top for club members), so
 * this only has to pick the club:
 *   - signed out → login, then straight back here;
 *   - one club → its Wallet;
 *   - several → the one whose card they hold, else My Clubs to choose.
 * Sponsors and platform staff's implicit memberships hold no card, so they
 * don't count as "their club" here.
 * SB-7: before falling back to My Clubs, use the last club viewed on this
 * device (Navbar remembers it) — Marakin staff, who hold only implicit
 * memberships, used to always land on My Clubs.
 */
export default function WalletRedirect() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true, state: { from: '/wallet' } }); return; }
    const clubs = (user.memberships || []).filter((m) => !m.implicit && m.role !== 'sponsor');
    // Only trust the remembered club if THIS person still has a non-sponsor
    // membership of it (implicit counts — staff; only active clubs are
    // listed), so a stale, deleted or other user's club never comes back.
    let lastClub = null;
    try { lastClub = localStorage.getItem('marakin_last_club'); } catch { /* storage unavailable */ }
    const known = lastClub && (user.memberships || []).some((m) => m.clubSlug === lastClub && m.role !== 'sponsor');
    if (lastClub && !known) { try { localStorage.removeItem('marakin_last_club'); } catch { /* storage unavailable */ } }
    const pick = clubs.length === 1 ? clubs[0] : clubs.find((m) => m.holdsCard);
    if (pick) navigate(`/${pick.clubSlug}/wallet`, { replace: true });
    else if (known) navigate(`/${lastClub}/wallet`, { replace: true });
    // Several clubs, no card: My Clubs asks which Wallet (state.next) rather
    // than dropping them on a hub after a shortcut that promised the card.
    else navigate('/my-clubs', { replace: true, state: { next: 'wallet' } });
  }, [user, loading, navigate]);

  return null;
}
